/**
 * Minimal MD5, hex output — byte-for-byte what legacy's `CryptoJS.MD5(email).toString()`
 * produces (MEP-1371).
 *
 * Written out rather than pulled from `crypto-js` on purpose: this ships inside the
 * library-wide global script, which loads on every product's pages, and those pages are
 * SEO-critical. `crypto-js` is CommonJS and does not tree-shake to just MD5, so the
 * dependency would cost far more than the ~40 lines below. WebCrypto is not an option
 * either — it offers no MD5, and its API is async while `dataLayer.push` is not.
 *
 * MD5 is used here only because the legacy contract uses it. It is a hashing choice
 * inherited for report compatibility, not a security control: the same event also
 * carries the raw email, exactly as legacy sends it.
 */

const S = [
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
  5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
  4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
  6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
];

const K = Array.from({ length: 64 }, (_, i) => Math.floor(Math.abs(Math.sin(i + 1)) * 4294967296));

const rotl = (x: number, c: number) => (x << c) | (x >>> (32 - c));

/** UTF-8 encodes the input, as CryptoJS does for a JS string. */
const toBytes = (input: string): number[] => Array.from(new TextEncoder().encode(input));

const toHexLE = (word: number): string => {
  let out = '';
  for (let i = 0; i < 4; i += 1) {
    out += ((word >>> (i * 8)) & 0xff).toString(16).padStart(2, '0');
  }
  return out;
};

export const md5 = (input: string): string => {
  const bytes = toBytes(input);
  const bitLen = bytes.length * 8;

  bytes.push(0x80);
  while (bytes.length % 64 !== 56) bytes.push(0);

  // 64-bit little-endian length; the high word is always 0 for the string sizes here.
  for (let i = 0; i < 4; i += 1) bytes.push((bitLen >>> (i * 8)) & 0xff);
  for (let i = 0; i < 4; i += 1) bytes.push(0);

  let [a0, b0, c0, d0] = [0x67452301, 0xefcdab89, 0x98badcfe, 0x10325476];

  for (let chunk = 0; chunk < bytes.length; chunk += 64) {
    const m: number[] = [];
    for (let i = 0; i < 16; i += 1) {
      const o = chunk + i * 4;
      m.push(bytes[o] | (bytes[o + 1] << 8) | (bytes[o + 2] << 16) | (bytes[o + 3] << 24));
    }

    let [a, b, c, d] = [a0, b0, c0, d0];

    for (let i = 0; i < 64; i += 1) {
      let f: number;
      let g: number;

      if (i < 16) {
        f = (b & c) | (~b & d);
        g = i;
      } else if (i < 32) {
        f = (d & b) | (~d & c);
        g = (5 * i + 1) % 16;
      } else if (i < 48) {
        f = b ^ c ^ d;
        g = (3 * i + 5) % 16;
      } else {
        f = c ^ (b | ~d);
        g = (7 * i) % 16;
      }

      f = (f + a + K[i] + m[g]) | 0;
      a = d;
      d = c;
      c = b;
      b = (b + rotl(f, S[i])) | 0;
    }

    a0 = (a0 + a) | 0;
    b0 = (b0 + b) | 0;
    c0 = (c0 + c) | 0;
    d0 = (d0 + d) | 0;
  }

  return toHexLE(a0) + toHexLE(b0) + toHexLE(c0) + toHexLE(d0);
};
